import { useState } from "react";
import { Link } from "react-router-dom";
import { vehicles, categories } from "@/mocks/vehicles";

export default function FeaturedVehicles() {
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered = activeCategory === "all"
    ? vehicles
    : vehicles.filter((v) => v.category === activeCategory);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-widest">Nossa Frota</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-1">Carros Disponíveis</h2>
          <p className="text-gray-500 mt-2">Escolha o veículo ideal para sua necessidade em Campinas e região</p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-500"
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-sm`}></i>
              </div>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Vehicle Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((vehicle) => (
            <Link
              key={vehicle.id}
              to={`/veiculo/${vehicle.id}`}
              className="bg-white border border-gray-100 rounded-2xl overflow-hidden group hover:border-orange-200 hover:shadow-md transition-all cursor-pointer"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden bg-gray-50">
                <img
                  src={vehicle.image}
                  alt={vehicle.name}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                {/* Availability Badge */}
                <div className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full ${
                  vehicle.available
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-600"
                }`}>
                  {vehicle.available ? "Disponível" : "Já Locado"}
                </div>
                {!vehicle.available && (
                  <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                    <span className="bg-red-500 text-white text-xs font-extrabold px-4 py-2 rounded-full tracking-widest uppercase">Indisponível</span>
                  </div>
                )}

              </div>

              {/* Info */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold text-gray-900 text-base">{vehicle.name}</h3>
                    <span className="text-xs text-orange-500 font-medium">{vehicle.category}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-yellow-400 text-xs"></i>
                    </div>
                    {vehicle.rating}
                  </div>
                </div>

                {/* Specs */}
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </div>
                    {vehicle.seats} lugares
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-settings-3-line"></i>
                    </div>
                    {vehicle.transmission}
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-gas-station-line"></i>
                    </div>
                    {vehicle.fuel}
                  </div>
                </div>

                {/* Price */}
                {vehicle.available ? (
                  <>
                    <div className="flex items-end justify-between mb-3">
                      <div>
                        {vehicle.originalPrice > vehicle.price && (
                          <span className="text-gray-400 text-xs line-through block">R$ {vehicle.originalPrice}/dia</span>
                        )}
                        <span className="text-orange-500 font-extrabold text-2xl">
                          R$ {vehicle.price}
                          <span className="text-sm font-normal text-gray-400">/dia</span>
                        </span>
                      </div>
                      <span className="bg-orange-500 text-white text-xs font-bold px-4 py-2 rounded-xl whitespace-nowrap">
                        Reservar
                      </span>
                    </div>

                    {/* Uber Promo */}
                    <div className="bg-black rounded-xl px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="bg-white text-black text-[10px] font-extrabold px-1.5 py-0.5 rounded-full">UBER</span>
                        <span className="bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">-40%</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white/50 text-[10px] line-through block leading-none">R$ {vehicle.monthlyPrice?.toLocaleString("pt-BR")}/mês</span>
                        <span className="text-white text-sm font-extrabold leading-none">R$ {vehicle.uberMonthlyPrice?.toLocaleString("pt-BR")}<span className="text-white/60 text-[10px] font-normal">/mês</span></span>
                      </div>
                    </div>
                    <a
                      href={`https://wa.me/5519987386173?text=${encodeURIComponent(`Olá! Tenho interesse em reservar o ${vehicle.name}. Poderia me passar mais informações?`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-bold py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-whatsapp-line text-base"></i>
                      </div>
                      Reservar via WhatsApp
                    </a>
                  </>
                ) : (
                  <div className="flex items-center justify-center py-4 bg-red-50 rounded-xl">
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-lock-line text-red-400 text-sm"></i>
                    </div>
                    <span className="text-red-400 text-sm font-semibold">Veículo já locado</span>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>


      </div>
    </section>
  );
}
